import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EVENT_POSTER_DISMISS_KEY, EVENT_POSTER_SIGNATURE, eventPosters } from './eventPosterConfig';
import BaseDetailSheet from '../game/components/BaseDetailSheet';

interface EventPosterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EventPosterModal: React.FC<EventPosterModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const posters = eventPosters.map((poster) => ({
    ...poster,
    title: t(poster.titleKey),
    alt: t(poster.altKey ?? poster.titleKey)
  }));
  const hasMultiplePosters = posters.length > 1;

  // 控制淡入淡出動畫
  useEffect(() => {
    if (isOpen) {
      const dismissedSignature = localStorage.getItem(EVENT_POSTER_DISMISS_KEY);
      setDontShowAgain(dismissedSignature === EVENT_POSTER_SIGNATURE);
      setCurrentSlide(0);
      setIsDragging(false);
      setStartX(null);

      if (hasMultiplePosters) {
        const timer = setTimeout(() => setCurrentSlide(1), 3000);
        return () => {
          clearTimeout(timer);
        };
      }

      return undefined;
    }
    return undefined;
  }, [hasMultiplePosters, isOpen]);

  // 控制背景滾動
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handlePosterClick = (slideIndex = currentSlide) => {
    const targetPath = posters[slideIndex]?.path;
    if (targetPath) {
      navigate(targetPath);
    }
    onClose();
  };

  const handleCheckboxChange = (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked) {
      localStorage.setItem(EVENT_POSTER_DISMISS_KEY, EVENT_POSTER_SIGNATURE);
    } else {
      localStorage.removeItem(EVENT_POSTER_DISMISS_KEY);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide((prev) => {
      const nextIndex = Math.min(Math.max(index, 0), posters.length - 1);
      return nextIndex === prev ? prev : nextIndex;
    });
  };

  const handleTouchStart = (clientX: number) => {
    setStartX(clientX);
    setIsDragging(false);
  };

  const handleTouchMove = (clientX: number) => {
    if (startX === null) return;
    if (Math.abs(clientX - startX) > 10) {
      setIsDragging(true);
    }
  };

  const handleTouchEnd = (clientX: number) => {
    if (startX === null) return;
    const delta = clientX - startX;
    const threshold = 50;

    if (delta > threshold) {
      goToSlide(currentSlide - 1);
    } else if (delta < -threshold) {
      goToSlide(currentSlide + 1);
    } else if (!isDragging) {
      handlePosterClick();
    }

    setStartX(null);
    setIsDragging(false);
  };

  const handlePointerDown = (event: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in event) {
      handleTouchStart(event.touches[0].clientX);
    } else {
      handleTouchStart(event.clientX);
    }
  };

  const handlePointerMove = (event: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in event) {
      handleTouchMove(event.touches[0].clientX);
    } else if (startX !== null) {
      handleTouchMove(event.clientX);
    }
  };

  const handlePointerUp = (event: React.MouseEvent | React.TouchEvent) => {
    if ('changedTouches' in event) {
      handleTouchEnd(event.changedTouches[0].clientX);
    } else if (startX !== null) {
      handleTouchEnd(event.clientX);
    }
  };

  return (
    <BaseDetailSheet isOpen={isOpen} onClose={onClose} maxWidthClass="max-w-2xl" zIndex={60}>
      <div
        className={`relative -m-5 flex flex-col shadow-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-blue-500/10 to-pink-500/20 pointer-events-none" />
        <div className="relative flex flex-col h-full bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/95 border border-purple-500/40 backdrop-blur-xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex flex-col flex-1 min-h-0 p-4 sm:p-6">
            <div className="mb-4 text-center text-lg font-semibold text-purple-100 sm:text-xl">
              {posters[currentSlide]?.title}
            </div>
            <div
              className="relative flex-1 min-h-0 overflow-hidden rounded-xl border border-purple-500/30 bg-black/30 shadow-lg"
              onMouseDown={handlePointerDown}
              onMouseMove={handlePointerMove}
              onMouseUp={handlePointerUp}
              onMouseLeave={() => {
                if (startX !== null) {
                  setStartX(null);
                  setIsDragging(false);
                }
              }}
              onTouchStart={handlePointerDown}
              onTouchMove={handlePointerMove}
              onTouchEnd={handlePointerUp}
            >
              <div
                className="relative flex h-full w-full select-none touch-pan-y"
                style={{ transform: `translateX(-${currentSlide * 100}%)`, transition: 'transform 300ms ease' }}
              >
                {posters.map((poster, index) => (
                  <div key={poster.id} className="h-full w-full flex-shrink-0">
                    <div className="relative h-full w-full overflow-hidden">
                      <img
                        src={poster.src}
                        alt={poster.alt}
                        className="h-full w-full cursor-pointer object-cover transition duration-300 hover:scale-[1.02]"
                        draggable={false}
                        onClick={() => {
                          if (!isDragging) {
                            handlePosterClick(index);
                          }
                        }}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/30 opacity-60" />
                      <div className="pointer-events-none absolute inset-0 ring-1 ring-purple-500/40" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2 text-sm text-gray-200 sm:text-base">
              <label className="flex cursor-pointer items-center gap-2" htmlFor="event-poster-dismiss">
                <input
                  id="event-poster-dismiss"
                  type="checkbox"
                  className="h-5 w-5 rounded border-purple-400/60 bg-black/40 text-purple-400 focus:ring-purple-400"
                  checked={dontShowAgain}
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                />
                <span>{t('eventPosterModal.dontShowAgain')}</span>
              </label>

              {hasMultiplePosters && (
                <div className="ml-auto flex items-center gap-2 text-xs sm:text-sm">
                  {posters.map((_, index) => (
                    <button
                      key={index}
                      aria-label={`Go to slide ${index + 1}`}
                      className={`h-2.5 w-2.5 rounded-full transition ${
                        currentSlide === index ? 'bg-purple-300 shadow-[0_0_0_4px_rgba(168,85,247,0.25)]' : 'bg-white/40 hover:bg-white/70'
                      }`}
                      onClick={() => goToSlide(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseDetailSheet>
  );
};

export default EventPosterModal;
